import React from 'react';

import {
  Card,
  FormItem,
  EditableInputModal,
  AutoComplete,
  Toggle,
  Dropdown,
  MultiInputField,
} from '../../../components';
import { Input } from '../../../components/Input';
import {
  validateDomains,
  nonEmptyStringArrayValidator,
} from '../utils/validators';
import type { AgentForm as AgentFormType } from '../../../utilities';

export type SmartActionsFormInputsProps = {
  children?: React.ReactNode;
  form: AgentFormType;
  disabled?: boolean;
  knowledgeData?: OptionType[];
};

const SmartActionsFormInputs: React.FC<SmartActionsFormInputsProps> = ({
  children,
  disabled,
  knowledgeData,
}) => {
  return (
    <div className="flex flex-col gap-y-md">
      {/* Prompts, Code & Web hooks */}
      <Card
        collapsable
        size="unset"
        title="Events Web hooks"
        name="agentpage.Events Web hooks"
        contentClassName="flex flex-col gap-y-md"
        subtitle="This section allows you to configure webhooks for some events"
      >
        <FormItem<AgentFormType>
          name="conversation_metadata_extractor_prompt"
          label="End of conversation metadata extractor prompt"
        >
          <EditableInputModal multiline disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="conversation_metrics_prompt"
          label="Conversation metrics prompt"
        >
          <EditableInputModal multiline disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="pii_masking_prompt"
          label="PII masking prompt"
        >
          <EditableInputModal multiline disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="end_of_conversation_event_handler_endpoint"
          label="End of conversation event handler endpoint"
        >
          <Input disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="on_agent_init_n8n_webhook"
          label="On agent init N8N web hook"
          validate={validateDomains()}
        >
          <Input disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="custom_tool_configurations"
          label="Custom Tool Configurations"
        >
          <Input
            disabled={disabled}
            multiline
            inputClassName="font-mono text-mainText text-small"
          />
        </FormItem>
        <FormItem<AgentFormType>
          name="n8n_whatsup_url"
          label="N8N WhatsApp URL"
        >
          <Input disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="on_phone_call_started_webhook"
          label="On phone call started webhook"
        >
          <Input disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="on_phone_call_ended_webhook"
          label="On phone call ended webhook"
        >
          <Input disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="use_datetime_tool"
          label="Use Get Current DateTime Tool"
        >
          <Toggle disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="use_terminate_call_tool"
          label="Use Terminate Conversation Tool"
        >
          <Toggle disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="trigger_next_agent_response"
          label="When enabled - you may prompt the agent to decide when to trigger next response again"
        >
          <Toggle disabled={disabled} />
        </FormItem>
      </Card>

      {/* RAG Settings */}
      <Card
        collapsable
        size="unset"
        title="RAG Settings"
        name="agentpage.RAG Settings"
        contentClassName="flex flex-col gap-y-md"
        subtitle=""
      >
        <FormItem<AgentFormType>
          name="rag_collection_id"
          label="Knowledge Base"
        >
          <AutoComplete options={knowledgeData || []} />
        </FormItem>
        <FormItem<AgentFormType> name="is_rag_enabled" label="Is RAG Enabled">
          <Input disabled={disabled} readonly />
        </FormItem>
        <FormItem<AgentFormType>
          name="rag_similarity_threshold"
          label="Similarity Threshold"
        >
          <Input disabled={disabled} numeric />
        </FormItem>
        <FormItem<AgentFormType>
          name="rag_max_response_chunks"
          label="Max Response Chunks"
        >
          <Input disabled={disabled} numeric />
        </FormItem>
      </Card>
      {/* Perplexity Settings */}
      <Card
        collapsable
        size="unset"
        title="Perplexity Settings"
        name="agentpage.Perplexity Settings"
        contentClassName="flex flex-col gap-y-md"
        subtitle=""
      >
        <FormItem<AgentFormType>
          name="use_perplexity"
          label="Perplexity Support"
        >
          <Toggle disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType> name="ppx_model" label="Model">
          <Input disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType> name="ppx_temperature" label="Temperature">
          <Input disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType> name="ppx_timeout_sec" label="Timeout (sec)">
          <Input disabled={disabled} numeric />
        </FormItem>
        <FormItem<AgentFormType>
          name="ppx_search_recency_filter"
          label="Search Recency Filter"
        >
          <AutoComplete
            disabled={disabled}
            options={[
              { value: 'day', label: 'Day' },
              { value: 'week', label: 'Week' },
              { value: 'month', label: 'Month' },
              { value: 'year', label: 'Year' },
            ]}
          />
        </FormItem>
        <FormItem<AgentFormType>
          name="ppx_search_context_size"
          label="Search Context Size"
        >
          <Dropdown
            disabled={disabled}
            options={[
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' },
            ]}
          />
        </FormItem>
        <FormItem<AgentFormType>
          name="ppx_enhanced_query"
          label="Enhanced Query"
        >
          <Input disabled={disabled} />
        </FormItem>
        <FormItem<AgentFormType>
          name="ppx_search_domain_filter"
          label="Search Domain Filter"
          validate={value => nonEmptyStringArrayValidator(value)}
          renderError={() => null}
        >
          <MultiInputField />
        </FormItem>
        <FormItem<AgentFormType> name="ppx_max_tokens" label="Max Tokens">
          <Input disabled={disabled} numeric />
        </FormItem>
      </Card>
      {children}
    </div>
  );
};

export default React.memo(
  SmartActionsFormInputs
) as typeof SmartActionsFormInputs;
